import { useCallback, useEffect, useState } from "react";
import { Download, FileSpreadsheet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { SaleForm, PAYMENT_LABELS } from "./SaleForm";

const input = "rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";
const ghost = "flex items-center gap-2 rounded-md border border-linen px-4 py-2 text-sm font-semibold text-forest transition-colors hover:border-forest";

function Stat({ label, value, testid }) {
  return <div data-testid={testid} className="rounded-lg border border-linen bg-ivory p-4"><p className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">{label}</p><p className="mt-1 font-serif text-2xl text-forest">{value}</p></div>;
}

function QuickBooksBox({ isOwner }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("QuickBooks settings saved"); } catch (err) { toast.error(apiError(err)); } };
  if (!s) return null;
  return (
    <details data-testid="qb-settings" className="rounded-lg border border-linen bg-ivory p-4 text-sm">
      <summary className="cursor-pointer font-semibold text-forest">QuickBooks Desktop setup</summary>
      <p className="mt-2 text-xs leading-relaxed text-moss">Download the .iif file, then in QuickBooks Desktop go to <strong>File → Utilities → Import → IIF Files</strong>. Each sale becomes a Sales Receipt. Make sure these account names exist in your Chart of Accounts first (QuickBooks will otherwise create them as bank accounts). Back up your company file before importing.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {[["qb_income_account", "Income account"], ["qb_deposit_account", "Deposit-to account"], ["qb_customer_default", "Default customer name"]].map(([k, l]) => (
          <div key={k}><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">{l}</label><input data-testid={`qb-${k}`} value={s[k]} disabled={!isOwner} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={`${input} mt-1 w-full disabled:opacity-60`} /></div>
        ))}
      </div>
      {isOwner && <button data-testid="qb-save-button" onClick={save} className={`${btn} mt-3`}>Save</button>}
    </details>
  );
}

export default function SalesTab() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const month = new Date().toISOString().slice(0, 7);
  const [range, setRange] = useState({ from: `${month}-01`, to: new Date().toISOString().slice(0, 10) });
  const [sales, setSales] = useState(null);
  const [summary, setSummary] = useState(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/sales", { params: { from_date: range.from, to_date: range.to } }).then((r) => setSales(r.data));
    api.get("/admin/sales/summary").then((r) => setSummary(r.data));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const download = async (ext) => {
    try {
      const res = await api.get(`/admin/sales/export.${ext}`, { params: { from_date: range.from, to_date: range.to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `releaf-sales-${range.from}-to-${range.to}.${ext}` });
      a.click(); URL.revokeObjectURL(url);
      if (ext === "iif") load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const void_ = async (s) => {
    if (!window.confirm(`Void sale ${s.number}? Stock and client records will be reversed.`)) return;
    try { await api.delete(`/admin/sales/${s.id}`); toast.success("Sale voided"); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const rangeTotal = (sales || []).reduce((t, s) => t + s.total, 0);

  return (
    <div data-testid="sales-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><h2 className="font-serif text-3xl font-medium text-forest">Sales</h2><p className="mt-1 text-sm text-moss">Record every sale, track daily and monthly totals, and export for your books.</p></div>
        <button data-testid="sale-new-button" onClick={() => setAdding(true)} className={btn}>+ New Sale</button>
      </div>

      {summary && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Today" value={`$${summary.today.toFixed(2)}`} testid="sales-stat-today" />
          <Stat label="This month" value={`$${summary.month.toFixed(2)}`} testid="sales-stat-month" />
          <Stat label="Sales this month" value={summary.month_count} testid="sales-stat-count" />
          <Stat label="Low-stock items" value={summary.low_stock} testid="sales-stat-lowstock" />
        </div>
      )}

      {adding && <div className="mt-6"><SaleForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} /></div>}

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">From</label><input data-testid="sales-from-input" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className={`${input} mt-1 block`} /></div>
        <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">To</label><input data-testid="sales-to-input" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className={`${input} mt-1 block`} /></div>
        <p className="ml-auto text-sm text-moss">{(sales || []).length} sales · <span className="font-semibold text-forest">${rangeTotal.toFixed(2)}</span></p>
        <button data-testid="sales-export-csv" onClick={() => download("csv")} className={ghost}><FileSpreadsheet size={15} /> CSV</button>
        <button data-testid="sales-export-iif" onClick={() => download("iif")} className={ghost}><Download size={15} /> QuickBooks IIF</button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-linen bg-ivory">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss"><tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Staff</th><th className="px-4 py-3 text-right">Total</th><th></th></tr></thead>
          <tbody className="divide-y divide-linen">
            {(sales || []).map((s) => (
              <tr key={s.id} data-testid={`sale-row-${s.number}`} className="cursor-pointer hover:bg-cream/60" onClick={() => setOpen(open === s.id ? null : s.id)}>
                <td className="px-4 py-3 font-medium text-forest">{s.number}{s.exported_at && <span className="ml-1 text-[10px] text-moss" title="Exported to QuickBooks">✓QB</span>}</td>
                <td className="px-4 py-3 text-moss">{s.date}</td>
                <td className="px-4 py-3 text-moss">{s.client_name || "Walk-in"}</td>
                <td className="px-4 py-3 text-moss">{open === s.id ? <ul className="space-y-0.5">{s.items.map((i, k) => <li key={k}>{i.name} × {i.qty} @ ${i.unit_price.toFixed(2)}</li>)}{s.discount > 0 && <li>Discount −${s.discount.toFixed(2)}</li>}{s.note && <li className="italic">{s.note}</li>}</ul> : `${s.items.length} item${s.items.length === 1 ? "" : "s"}`}</td>
                <td className="px-4 py-3 text-moss">{PAYMENT_LABELS[s.payment_method]}</td>
                <td className="px-4 py-3 text-xs text-moss">{s.staff_name}</td>
                <td className="px-4 py-3 text-right font-semibold text-forest">${s.total.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">{isOwner && <button data-testid={`sale-void-${s.number}`} onClick={(e) => { e.stopPropagation(); void_(s); }} className="text-moss hover:text-destructive" aria-label="Void"><Trash2 size={15} /></button>}</td>
              </tr>
            ))}
            {sales && sales.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-moss">No sales in this range.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-6"><QuickBooksBox isOwner={isOwner} /></div>
    </div>
  );
}
